import { inject, Injectable } from '@angular/core';

import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { finShare } from '@fcom/rx/index';
import {
  ApisStatusEnum,
  Category,
  FinnairApisRequirementKeyEnum,
  FinnairBoundItem,
  FinnairCheckInDictionary,
  FinnairCompletionStatus,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import { CheckinService as DapiCheckInService } from '@fcom/dapi/api/services/checkin.service';
import { getIconForServiceCategory } from '@fcom/dapi/utils';
import { ConfigService } from '@fcom/core/services';
import { DapiHttpResponse } from '@fcom/dapi/api/dapi-http-response';

import { isFlight, isNotIncludedService } from '../../common-booking/utils/common-booking.utils';
import { combineServices } from '../../common-booking/modules/ancillaries/utils';
import {
  CommonBookingFeatureState,
  FinnairServiceItemWithIcon,
  orderData,
  OrderStatus,
  orderStatus,
} from '../../common-booking';
import {
  getCheckInBoundId,
  getCheckInClearPendingSeatsOnEntry,
  getIsModifyCheckIn,
  getPassengers,
} from '../../manage-booking/store/selectors/check-in.selector';
import { ManageBookingFeatureState } from '../../manage-booking/interfaces';
import {
  findExitRowSeats,
  getPaxWithoutExitRowSeatConfirmation,
  getServicesForCheckInPassengers,
} from '../../manage-booking/utils/check-in.utils';
import {
  BoardingPassIncludedServicesRequest,
  EntryDocumentType,
  FinnairPassengerItemWithApis,
} from '../../manage-booking/modules/check-in/interface/check-in.interface';
import { CheckInActions } from '../../manage-booking/store/actions';
import { ExitRowSeatInfo } from '../../manage-booking/interfaces/checkin.interface';
import { updateIncludedSeats } from '../../manage-booking/utils/seat-util';
import {
  getCompletedEntryDocumentType,
  isApisRequirementListCompleted,
} from '../../manage-booking/modules/travel-preparations/components/apis-utils';

@Injectable({ providedIn: 'root' })
export class CheckInService {
  private store$ = inject<Store<CommonBookingFeatureState & ManageBookingFeatureState>>(Store);
  private dapiCheckInService = inject(DapiCheckInService);
  private configService = inject(ConfigService);

  private showSeatReassignmentSuccessfulMessage$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private showNoSeatsLeftNotificationMessage$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private initiateNextStep$: Subject<void> = new Subject<void>();

  private order$: Observable<FinnairOrder>;
  private orderStatus$: Observable<OrderStatus>;
  private bound$: Observable<FinnairBoundItem>;
  private seats$: Observable<FinnairServiceItem>;
  private modifyCheckIn$: Observable<boolean>;
  private selectedForCheckInPassengers$: Observable<FinnairPassengerItem[]>;
  private services$: Observable<FinnairServiceItemWithIcon[]>;
  private clearPendingSeatsOnEntry$: Observable<boolean>;
  private checkInDictionary$: Observable<FinnairCheckInDictionary>;

  private exitRowSeatsToConfirm$: Observable<ExitRowSeatInfo[]>;
  private confirmedExitRowSeatPassengerIds$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private isExitSeatsConfirmationRequired$: Observable<boolean>;
  private showExitRowSeatsNotification$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private completedEntryDocumentTypesForPassengers$: Observable<Record<string, EntryDocumentType>>;
  private entryDocumentSelection$: BehaviorSubject<Record<string, EntryDocumentType>> = new BehaviorSubject<
    Record<string, EntryDocumentType>
  >({});

  constructor() {
    this.modifyCheckIn$ = this.store$.pipe(getIsModifyCheckIn());
    this.clearPendingSeatsOnEntry$ = this.store$.pipe(getCheckInClearPendingSeatsOnEntry());
    this.order$ = this.store$.pipe(orderData()).pipe(finShare());
    this.checkInDictionary$ = this.order$.pipe(map((order) => order.checkIn?.dictionary));
    this.orderStatus$ = this.store$.pipe(orderStatus(), finShare());
    this.bound$ = this.store$.pipe(getCheckInBoundId()).pipe(
      switchMap((checkInBoundId) => {
        return this.order$.pipe(map(({ bounds }) => bounds.find((bound) => bound.id === checkInBoundId)));
      }),
      finShare()
    );
    this.seats$ = combineLatest([
      this.order$,
      this.bound$.pipe(
        map((bound) => bound.id),
        distinctUntilChanged()
      ),
    ]).pipe(
      map(([order, checkInBoundId]) => {
        const seats = combineServices([order?.services?.included || [], order?.services?.unpaid || []]).find(
          (s) => s.category === Category.SEAT
        );
        return {
          ...seats,
          bounds: seats?.bounds.filter((bound) => bound.id === checkInBoundId) ?? [],
        };
      }),
      finShare()
    );
    this.selectedForCheckInPassengers$ = combineLatest([
      this.order$.pipe(map(({ passengers }) => passengers)),
      this.store$.pipe(getPassengers()),
    ]).pipe(map(([passengers, passengerIds]) => passengers.filter(({ id }) => passengerIds.includes(id))));
    this.services$ = combineLatest([
      this.order$,
      this.selectedForCheckInPassengers$.pipe(map((pax) => pax.map((p) => p.id))),
    ]).pipe(
      map(([order, passengersInCheckIn]) => {
        const orderIncludedServices = getServicesForCheckInPassengers(
          order?.services?.included || [],
          passengersInCheckIn
        );
        const orderUnpaidServices = getServicesForCheckInPassengers(order?.services?.unpaid || [], passengersInCheckIn);

        return combineServices([orderIncludedServices, orderUnpaidServices]).map((s) => ({
          ...s,
          icon: getIconForServiceCategory(s.category),
        }));
      }),
      finShare()
    );
    this.exitRowSeatsToConfirm$ = combineLatest([
      this.seats$.pipe(
        withLatestFrom(this.selectedForCheckInPassengers$),
        map(([seats, passengerForCheckIn]) =>
          findExitRowSeats(
            seats,
            passengerForCheckIn.map((pax) => pax.id)
          )
        )
      ),
      this.confirmedExitRowSeatPassengerIds$,
    ]).pipe(
      map(([exitRowSeats, passengersWithConfirmation]) =>
        getPaxWithoutExitRowSeatConfirmation(exitRowSeats, passengersWithConfirmation)
      ),
      finShare()
    );
    this.isExitSeatsConfirmationRequired$ = this.exitRowSeatsToConfirm$.pipe(
      map((exitRowSeatsInfo) => exitRowSeatsInfo.flatMap((flight) => flight.passengers)),
      map((passengers) => passengers.length > 0)
    );
    this.completedEntryDocumentTypesForPassengers$ = this.getPassengersWithApis().pipe(
      map((passengers) => {
        return passengers.reduce(
          (completedEntryDocumentTypesForPassengers, passenger) => {
            completedEntryDocumentTypesForPassengers[passenger.id] = getCompletedEntryDocumentType(passenger);
            return completedEntryDocumentTypesForPassengers;
          },
          {} as Record<string, EntryDocumentType>
        );
      })
    );
  }

  getOrder(): Observable<FinnairOrder> {
    return this.order$;
  }

  getOrderStatus(): Observable<OrderStatus> {
    return this.orderStatus$;
  }

  getBound(): Observable<FinnairBoundItem> {
    return this.bound$;
  }

  isCarrierConnect(): Observable<boolean> {
    return combineLatest([this.order$, this.bound$]).pipe(
      map(([order, bound]) => {
        const boundEligibility = order.eligibilities?.checkIn?.find((eligibility) => eligibility.id === bound.id);
        return boundEligibility?.isCarrierConnect;
      }),
      finShare()
    );
  }

  isAPISRequired(): Observable<boolean> {
    return combineLatest([this.order$, this.bound$]).pipe(
      map(([order, bound]) => {
        const boundEligibility = order.eligibilities?.checkIn?.find((eligibility) => eligibility.id === bound.id);
        return boundEligibility.isApisRequired;
      }),
      finShare()
    );
  }

  isEligibleForVoluntaryDeniedBoarding(): Observable<boolean> {
    return combineLatest([this.order$, this.bound$]).pipe(
      map(([order, bound]) => {
        const checkInSegmentEligibilities = order.eligibilities?.checkIn?.find(
          (e) => e.id === bound.id
        )?.flightTravelerEligibility;
        if (!checkInSegmentEligibilities) {
          return false;
        }
        return Object.values(checkInSegmentEligibilities).every((eligibility) => {
          return (
            Array.isArray(eligibility) &&
            eligibility.some((e) => e.voluntaryDeniedBoarding?.isAllowedToUseVoluntaryDeniedBoarding)
          );
        });
      }),
      finShare()
    );
  }

  isVoluntaryDeniedBoarding(): Observable<boolean> {
    return combineLatest([this.order$, this.bound$]).pipe(
      map(([order, bound]) => {
        const checkInSegmentEligibilities = order.eligibilities?.checkIn?.find(
          (e) => e.id === bound.id
        )?.flightTravelerEligibility;
        if (!checkInSegmentEligibilities) {
          return false;
        }
        return Object.values(checkInSegmentEligibilities).every((eligibility) => {
          return Array.isArray(eligibility) && eligibility.some((e) => e.voluntaryDeniedBoarding?.isOptedIn);
        });
      }),
      finShare()
    );
  }

  isApisCompleted(): Observable<boolean> {
    return combineLatest([
      this.order$,
      this.bound$.pipe(map((bound) => bound.id)),
      this.selectedForCheckInPassengers$,
    ]).pipe(
      map(([order, boundId, passengers]) => {
        const passengerIds = passengers.map(({ id }) => id);
        const allCheckinPassengersCompleted = order.advancePassengerInformation?.[boundId]
          ? Object.entries(order.advancePassengerInformation[boundId])
              .filter(([paxId]) => passengerIds.includes(paxId))
              .every(([, requirement]) => requirement.status === FinnairCompletionStatus.COMPLETED)
          : false;

        const checkInSegmentEligibilities =
          order.eligibilities?.checkIn?.find((e) => e.id === boundId)?.flightTravelerEligibility ?? {};
        const allCheckinPassengersEligibleForAcceptance = Object.values(checkInSegmentEligibilities).every(
          (eligibility) => {
            const allowedEligibilities = eligibility.filter((e) => e.isAllowedToAcceptCheckIn);
            return (
              allowedEligibilities.length > 0 &&
              allowedEligibilities.every((e) => passengerIds.every((paxId) => e.travelerIds.includes(paxId)))
            );
          }
        );
        return allCheckinPassengersCompleted && allCheckinPassengersEligibleForAcceptance;
      }),
      finShare()
    );
  }

  isModifyCheckIn(): Observable<boolean> {
    return this.modifyCheckIn$;
  }

  setModifyCheckIn(modifyCheckIn: boolean): void {
    this.store$.dispatch(CheckInActions.setModifyCheckIn({ isModifyCheckIn: modifyCheckIn }));
  }

  getSeats(): Observable<FinnairServiceItem> {
    return this.seats$;
  }

  getServices(): Observable<FinnairServiceItemWithIcon[]> {
    return this.services$;
  }

  getPassengers(): Observable<FinnairPassengerItem[]> {
    return this.selectedForCheckInPassengers$;
  }

  getPassengersWithApis(): Observable<FinnairPassengerItemWithApis[]> {
    return combineLatest([
      this.selectedForCheckInPassengers$,
      this.bound$.pipe(map((bound) => bound.id)),
      this.order$,
    ]).pipe(
      map(([passengers, boundId, order]) => {
        const checkInEligibilityForBound = order?.eligibilities?.checkIn?.find(
          (eligibility) => eligibility.id === boundId
        );
        const checkinFlightEligibilities = checkInEligibilityForBound?.flightTravelerEligibility ?? {};

        return passengers.map((passenger) => {
          const eligibilities = Object.entries(checkinFlightEligibilities)
            .flatMap(([flightId, checkInFlightEligibility]) => {
              const eligibility = checkInFlightEligibility.find((e) => e.travelerIds.includes(passenger.id));

              return eligibility
                ? {
                    ...eligibility,
                    flightId,
                  }
                : undefined;
            })
            .filter(Boolean);

          const paxApisCpr = order.advancePassengerInformation?.[boundId]?.[passenger.id] ?? undefined;

          const passengerWithApis: FinnairPassengerItemWithApis = {
            ...passenger,
            apisCpr: paxApisCpr,
            dcsTravelerId: order.checkIn?.dictionary?.traveler[passenger.id],
            isInAgentReval:
              (eligibilities ?? []).some((flight) => !flight.isAllowedToAcceptCheckIn) &&
              paxApisCpr?.apisStatus === ApisStatusEnum.AGENT_REVAL_REQUIRED &&
              paxApisCpr?.requirements.every((r) => !r.required || r.status === FinnairCompletionStatus.COMPLETED) ===
                true,
            eligibilities,
          };

          const isAllowedToAcceptCheckIn = eligibilities.some((flight) => flight.isAllowedToAcceptCheckIn);
          const informationsCompleteOrNotRequired =
            paxApisCpr?.apisStatus === ApisStatusEnum.COMPLETE ||
            paxApisCpr?.apisStatus === ApisStatusEnum.NOT_REQUIRED ||
            isAllowedToAcceptCheckIn;
          const completed = isApisRequirementListCompleted(passengerWithApis) && informationsCompleteOrNotRequired;
          const completedEntryDocumentType = getCompletedEntryDocumentType(passengerWithApis);
          const verifierDetails = {
            completed,
            started:
              paxApisCpr?.requirements.some((r) => r.required && r.status === FinnairCompletionStatus.COMPLETED) ??
              false,
            passportSwiped:
              paxApisCpr?.requirements.find((r) => r.key === FinnairApisRequirementKeyEnum.PASSPORT)?.swiped ?? false,
            completedEntryDocumentType,
            entryDocumentSwiped:
              completedEntryDocumentType &&
              paxApisCpr?.requirements.find(
                (r) =>
                  r.key ===
                  (completedEntryDocumentType === EntryDocumentType.VISA
                    ? FinnairApisRequirementKeyEnum.VISA
                    : FinnairApisRequirementKeyEnum.PERMANENT_RESIDENT_CARD)
              )?.swiped,
          };
          return {
            ...passengerWithApis,
            verifierDetails,
          };
        });
      })
    );
  }

  getClearPendingSeatsOnEntry(): Observable<boolean> {
    return this.clearPendingSeatsOnEntry$;
  }

  setClearPendingSeatsOnEntry(clearSeatsOnEntry: boolean): void {
    this.store$.dispatch(CheckInActions.setClearPendingSeatsOnEntry({ clearPendingSeatsOnEntry: clearSeatsOnEntry }));
  }

  getCompletedEntryDocumentTypeForPassenger(passengerId: string): Observable<EntryDocumentType | undefined> {
    return this.completedEntryDocumentTypesForPassengers$.pipe(
      map((completedEntryDocumentTypesForPassengers) => {
        return completedEntryDocumentTypesForPassengers[passengerId];
      })
    );
  }

  selectEntryDocumentType(passengerId: string, type: EntryDocumentType): void {
    const current = this.entryDocumentSelection$.value;

    this.entryDocumentSelection$.next({
      ...current,
      [passengerId]: type,
    });
  }

  getEntryDocumentTypeSelectionForPassenger(passengerId: string): Observable<EntryDocumentType> {
    return this.entryDocumentSelection$.pipe(map((selectionMap) => selectionMap[passengerId]));
  }

  getShowSeatsReassignmentSuccessfulMessage(): Observable<boolean> {
    return this.showSeatReassignmentSuccessfulMessage$.asObservable();
  }

  getShowNoSeatsLeftNotificationMessage(): Observable<boolean> {
    return this.showNoSeatsLeftNotificationMessage$.asObservable();
  }

  setShowSeatsReassignmentSuccessfulMessage(showMessage: boolean): void {
    this.showSeatReassignmentSuccessfulMessage$.next(showMessage);
  }

  setShowNoSeatsLeftNotificationMessage$(showMessage: boolean): void {
    this.showNoSeatsLeftNotificationMessage$.next(showMessage);
  }

  setConfirmedExitRowSeatPassengers(passengerIds: string[]): void {
    this.confirmedExitRowSeatPassengerIds$.next(passengerIds);
  }

  getExitRowSeatsToConfirm(): Observable<ExitRowSeatInfo[]> {
    return this.exitRowSeatsToConfirm$;
  }

  isExitRowSeatConfirmationRequired(): Observable<boolean> {
    return this.isExitSeatsConfirmationRequired$;
  }

  isExitRowSeatConfirmationRequiredForPassengers(passengerIds: string[]): Observable<boolean> {
    return combineLatest([
      this.seats$.pipe(map((seats) => findExitRowSeats(seats, passengerIds))),
      this.confirmedExitRowSeatPassengerIds$,
    ]).pipe(
      map(
        ([exitRowSeats, passengersWithConfirmation]) =>
          getPaxWithoutExitRowSeatConfirmation(exitRowSeats, passengersWithConfirmation).flatMap(
            (flight) => flight.passengers
          ).length > 0
      ),
      finShare()
    );
  }

  getShowExitRowSeatsNotification(): Observable<boolean> {
    return this.showExitRowSeatsNotification$.asObservable();
  }

  setShowExitRowSeatsNotification(showExitRowSeatsNotification: boolean): void {
    this.showExitRowSeatsNotification$.next(showExitRowSeatsNotification);
  }

  getGoToNextCheckInStepTrigger(): Observable<void> {
    return this.initiateNextStep$;
  }

  triggerNextCheckInStep(): void {
    this.initiateNextStep$.next();
  }

  /**
   * This is used for CarrierConnect seat number optimization as we don't get them anywhere else than from the boarding pass response
   * and we need to update the seat numbers after going to the boarding pass view.
   */
  getUpdatedIncludedServicesFromRawBoardingPass$(): Observable<BoardingPassIncludedServicesRequest> {
    return combineLatest([this.getOrder(), this.getBound()]).pipe(
      take(1),
      switchMap(([order, bound]) => {
        const hasSeatsToDisplay =
          order.services?.included
            ?.find((c) => c.category === Category.SEAT)
            ?.bounds.find((orderBound) => orderBound.id === bound.id)
            ?.segments?.some((segment) =>
              segment.passengers.some((pax) => (pax.services ?? []).filter(isNotIncludedService)?.length > 0)
            ) ?? false;

        if (
          order.eligibilities?.checkIn?.find((eligibility) => eligibility.id === bound.id)?.isCarrierConnect !== true ||
          hasSeatsToDisplay
        ) {
          return of({
            loading: false,
          });
        }

        const flights = bound.itinerary.filter(isFlight);

        const journeyElementIds = flights.flatMap((flight) => {
          return Object.entries(order.checkIn?.dictionary.journeyElement[flight.id] ?? {})
            .filter(([passengerId]) => {
              const travelerEligibility =
                order.eligibilities.checkIn?.find((eligibility) => eligibility.id === bound.id)
                  ?.flightTravelerEligibility ?? {};
              return (
                travelerEligibility?.[flight.id]?.find((e) => e.travelerIds.includes(passengerId))
                  ?.isAllowedToUseBoardingPass ?? false
              );
            })
            .map(([, journeyElementId]) => journeyElementId);
        });

        const journeys =
          journeyElementIds.length > 0
            ? [
                {
                  journeyId: order.checkIn?.dictionary.journey[bound.id],
                  journeyElementIds,
                },
              ]
            : [];

        if (journeys.length === 0) {
          return of({ loading: false });
        }

        return this.dapiCheckInService
          .getRawBoardingPass(this.configService.cfg.orderUrl, {
            orderId: order.id,
            hash: order.hash,
            body: journeys,
          })
          .pipe(
            map((boardingPassResponse) => {
              const seatAssignments = Object.entries(order.checkIn?.dictionary.journeyElement ?? {}).flatMap(
                ([segmentId, passengers]) =>
                  Object.entries(passengers)
                    .map(([passengerId, journeyElementId]) => {
                      const seatNumber = boardingPassResponse
                        .filter((bp) => bp.success)
                        .find((bp) => bp.journeyElementId === journeyElementId)?.data?.segment?.seat?.seatNumber;

                      return seatNumber
                        ? {
                            seatNumber,
                            segmentId,
                            passengerId,
                            boundId: bound.id,
                          }
                        : null;
                    })
                    .filter(Boolean)
              );

              return {
                loading: false,
                data: updateIncludedSeats(order, order.services.included, seatAssignments),
              };
            }),
            catchError((error: unknown) => of({ loading: false, error })),
            startWith({ loading: true })
          );
      }),
      finShare()
    );
  }

  confirmVoluntaryDeniedBoarding(orderId: string, hash: string, journeyId: string): Observable<DapiHttpResponse<void>> {
    return this.dapiCheckInService.confirmVoluntaryDeniedBoarding$Response(this.configService.cfg.orderUrl, {
      orderId,
      hash,
      body: [journeyId],
    });
  }

  getJourneyId(): Observable<string> {
    return combineLatest([this.bound$, this.checkInDictionary$]).pipe(
      map(([bound, dictionary]) => dictionary?.journey[bound.id])
    );
  }
}
